import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import { getAisle } from 'api/jalapeno';
import useAccountQuery from 'api/queries/useAccountQuery';
import AisleContent from 'features/shop/AisleContent';
import { dietaryFilterData } from 'features/shop/filters/DietaryFilters/DietaryFiltersData';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { scrollToL2AisleElement } from 'utils/aislesUtils';
import { linkAisleBannersToProductCarousels } from 'utils/bannerCarouselLinkUtils';
import { filterProducts, getAvailableFilters } from 'utils/filterUtils';
import { getDecodedPathSegment } from 'utils/pathUtils';

function Aisle() {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const { data: account } = useAccountQuery();
    const aisles = useOutletContext();
    const { appliedFilters } = useGlobalStore();
    const navigate = useNavigate();

    const params = useParams();
    const aisleDisplayName = getDecodedPathSegment(params?.category);
    const categoryDisplayName = getDecodedPathSegment(params?.subcategory) || 'all';

    const currentAisle = aisles?.find((aisle) => aisle.name.toLowerCase() === aisleDisplayName?.toLowerCase());
    const currentCategory = currentAisle?.aisles?.find(
        (category) => category.name.toLowerCase() === categoryDisplayName?.toLowerCase()
    ) || {
        id: categoryDisplayName,
        name: 'All',
    };

    useEffect(() => {
        if (!currentAisle) {
            // TODO: CUS-5632 we can remove aisle specific redirects when email/ad/external links are updated
            if (params?.category === 'prepared+foods') {
                navigate('/shop/aisle/prepared/all');
            } else if (params?.category === 'saved+&+rescued') {
                navigate('/shop/aisle/rescued/all');
            } else if (params?.category === 'vitamins+&+supplements') {
                navigate('/shop/aisle/wellness/all');
            } else if (params?.category === 'plant-based+shop') {
                navigate('/shop/aisle/plant-based/all');
            } else if (params?.category === 'gluten-free+shop') {
                navigate('/shop/aisle/gluten-free/all');
            } else if (params?.category === 'discover+something+new') {
                navigate('/shop/aisle/new+picks/all');
            } else {
                navigate('/shop');
            }
        }
    }, [currentAisle?.id, currentCategory.id]);

    const aisleQuery = useQuery({
        queryKey: ['aisle', currentAisle?.id],
        queryFn: async () => {
            const response = await getAisle(account?.nextOrder?.chargeID, currentAisle?.id);

            if (response?.data) {
                linkAisleBannersToProductCarousels(response.data, { aisleID: currentAisle?.id });
            }

            return response;
        },
        enabled: account?.nextOrder?.chargeID > 0 && currentAisle?.id > 0,
    });

    const { isLoading } = aisleQuery;

    useEffect(() => {
        if (!isLoading && currentCategory.id !== 'all') {
            scrollToL2AisleElement({ mobile, selector: `#aisle-${currentCategory.id}` });
        }
    }, [isLoading]);

    const { data: aisleData } = aisleQuery?.data ?? {};
    const { carouselV2: carouselV2Data, items, subAisles } = aisleData ?? {};

    const dietaryFilteredItems = filterProducts(appliedFilters, subAisles, items);
    const finalFilteredItems = dietaryFilteredItems;
    const availableFilters = getAvailableFilters(dietaryFilterData, subAisles, items);

    if (aisleQuery.error) return null;

    return (
        <AisleContent
            isLoading={isLoading}
            categories={subAisles?.length > 0 ? finalFilteredItems : []}
            carouselV2Data={carouselV2Data}
            banners={aisleData?.banners}
            currentAisle={currentAisle}
            currentCategory={currentCategory}
            filtersData={availableFilters}
            items={finalFilteredItems}
            otherContent={aisleData?.otherContent}
        />
    );
}

export { Aisle as Component };
